import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchApi } from '@/shared/api/fetchApi';

export interface CarCalendarPrice {
  is_active: boolean;
  active_at: string;
  price_default: string;
  price: string;
  discount: string;
  is_promotion: boolean;
  description: string;
}

export interface CarCalendarResponse {
  prices: CarCalendarPrice[];
  rents: any[]; // We can type this more specifically if needed
}

export interface EditPriceRequest {
  car: string;
  from_dt: string;
  till_dt: string;
  price_default: number;
  discount: number;
  is_promotion: boolean;
  description: string;
}

export interface EditPriceResponse {
  data: CarCalendarPrice[];
}

export interface PriceHint {
    active_at: string;
    source: string;
    name: string;
    daily_price: string | null;
    avg_month_price: string | null;
    external_car: string;
    car_model: string;
}

interface PriceHintResponse {
  data: PriceHint[];
}

interface PriceHintRequest {
  date: string;
  car: string;
  search_string?: string;
}

export const useCarCalendarPrices = (carId: string, year: number, month: number) => {
  return useQuery({
    queryKey: ['carCalendar', carId, year, month],
    queryFn: async () => {
      return fetchApi<CarCalendarResponse>(
        `/api/v1/calendar/car?car=${carId}&year=${year}&month=${month}`
      );
    },
  });
};

export const useEditCarPrice = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: EditPriceRequest) => {
      return fetchApi<EditPriceResponse>('/api/v1/calendar/car/edit_prices', {
        method: 'POST',
        body: JSON.stringify(request),
      });
    },
    onSuccess: (_, variables) => {
      // Invalidate relevant queries to trigger a refetch
      const date = new Date(variables.from_dt);
      queryClient.invalidateQueries({
        queryKey: ['carCalendar', variables.car, date.getFullYear(), date.getMonth() + 1],
      });
    },
  });
};

export interface TogglePriceRequest {
  car: string;
  from_dt: string;
  till_dt: string;
  is_active: boolean;
}

export const useToggleCarPrice = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: TogglePriceRequest) => {
      return fetchApi('/api/v1/calendar/car/toggle_prices', {
        method: 'POST',
        body: JSON.stringify(request),
      });
    },
    onSuccess: (_, variables) => {
      const date = new Date(variables.from_dt);
      queryClient.invalidateQueries({
        queryKey: ['carCalendar', variables.car, date.getFullYear(), date.getMonth() + 1],
      });
    },
  });
};

export const useGetPriceHints = () => {
  return useMutation({
    mutationFn: async (data: PriceHintRequest) => {
      return fetchApi<PriceHintResponse>('/api/v1/calendar/price_hint', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },
  });
};
